import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useRole } from "../context/RoleContext";
import { listMyDesigns, openNewVersion } from "../lib/architectPortalB";
import { assetUrl } from "../lib/format";
import {
  Plus,
  FileText,
  Search,
  ArrowUpRight,
  TrendingUp,
  Pencil,
  Layers,
} from "lucide-react";
import WorkflowStrip from "../components/WorkflowStrip";
import StatusPill from "../components/StatusPill";
import { buildFilterBar } from "../lib/statusFilters";

const FILTERS = buildFilterBar([
  "Draft",
  "In Review",
  "Needs Revision",
  "Approved",
  "Rejected",
]);

const FILTER_EMPTY_COPY = {
  Draft: "No designs in draft.",
  "In Review": "No designs in review.",
  "Needs Revision": "No designs need revision.",
  Approved: "No approved designs yet.",
  Rejected: "No rejected designs.",
  all: "You haven't submitted any designs yet.",
};

export default function ArchitectDesigns() {
  const { user } = useRole();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim().toLowerCase()), 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    listMyDesigns(user.id)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setErr(e));
    return () => {
      alive = false;
    };
  }, [user?.id]);

  // Client-side filter + search (catalog is small; no need to refetch).
  const filtered = useMemo(() => {
    if (!data) return [];
    return data.items.filter((d) => {
      if (filter !== "all" && d.status !== filter) return false;
      if (!debouncedQ) return true;
      const hay = [d.name, d.style, d.typology, ...(d.lifestyle_tags || [])]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(debouncedQ);
    });
  }, [data, filter, debouncedQ]);

  // Filter chip counts scoped to active search.
  const counts = useMemo(() => {
    if (!data) return { all: 0 };
    const matchesSearch = (d) => {
      if (!debouncedQ) return true;
      const hay = [d.name, d.style, d.typology, ...(d.lifestyle_tags || [])]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(debouncedQ);
    };
    const c = { all: 0 };
    for (const d of data.items) {
      if (!matchesSearch(d)) continue;
      c.all += 1;
      c[d.status] = (c[d.status] || 0) + 1;
    }
    return c;
  }, [data, debouncedQ]);

  const hasResults = data && filtered.length > 0;
  const isSearching = debouncedQ.length > 0;

  return (
    <section
      className="animate-fade-in-up"
      data-testid="architect-designs-page"
    >
      {/* ── Header ──────────────────────────────────────────────── */}
      <div
        className="flex items-end justify-between flex-wrap"
        style={{ gap: 24, paddingTop: 8, paddingBottom: 28 }}
      >
        <div style={{ minWidth: 0 }}>
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              style={{
                display: "inline-block",
                width: 22,
                height: 2,
                background: "#B14E2F",
              }}
            />
            <span className="wn-label" style={{ color: "#8F3C23" }}>
              Catalog
            </span>
          </div>
          <h1
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: "clamp(24px, 3.2vw, 34px)",
              fontWeight: 500,
              lineHeight: 1.1,
              letterSpacing: "-0.01em",
              color: "#1B1F4B",
              margin: "12px 0 0",
            }}
            data-testid="designs-h1"
          >
            Your designs.
          </h1>
          <div
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 14,
              lineHeight: 1.55,
              marginTop: 8,
            }}
          >
            {data ? summaryLine(data, filter, isSearching, debouncedQ, filtered.length) : " "}
          </div>
        </div>
        <button
          className="wn-btn wn-btn-primary"
          onClick={() => navigate("/architect/designs/new")}
          data-testid="submit-new-design-btn"
        >
          <Plus size={14} /> Submit a new design
        </button>
      </div>

      {/* ── Workflow legend ─────────────────────────────────────── */}
      <SectionHeader eyebrow="Review pipeline" title="The design path." />
      <div className="mt-4">
        <WorkflowStrip
          domain="design"
          current={null}
          compact
          testId="designs-workflow-legend"
        />
      </div>

      {/* ── Filter chips + search ──────────────────────────────── */}
      <div
        className="flex flex-wrap items-center"
        style={{ gap: 10, marginTop: 32 }}
      >
        <div className="flex flex-wrap gap-2" data-testid="designs-filters">
          {FILTERS.map((f) => {
            const isOn = filter === f.key;
            const n = f.key === "all" ? counts.all : counts[f.key] || 0;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                data-testid={`designs-filter-${f.key.replace(/\s+/g, "_")}`}
                style={pillStyle(isOn)}
              >
                {f.label}
                <span
                  className="tabular-nums"
                  style={{ marginLeft: 6, opacity: 0.7 }}
                >
                  · {n}
                </span>
              </button>
            );
          })}
        </div>
        <div
          className="flex items-center gap-2 wn-card"
          style={{
            padding: "0 12px",
            height: 34,
            minWidth: 280,
            marginLeft: "auto",
          }}
        >
          <Search size={14} style={{ color: "#9AA0C0" }} />
          <input
            data-testid="designs-search"
            placeholder="Search by name, style, or typology…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{
              border: 0,
              outline: 0,
              fontSize: 13,
              background: "transparent",
              width: "100%",
              fontFamily: '"Nunito Sans", sans-serif',
              color: "#272636",
            }}
          />
        </div>
      </div>

      {err && (
        <p
          className="mt-8"
          style={{ color: "#A23B2E" }}
          data-testid="designs-error"
        >
          Could not load your designs.
        </p>
      )}

      {!data && !err && (
        <div className="mt-6 space-y-2" data-testid="designs-loading">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="shimmer"
              style={{ height: 90, borderRadius: 8 }}
            />
          ))}
        </div>
      )}

      {data && !hasResults && (
        <EmptyStateCard
          filter={filter}
          isSearching={isSearching}
          searchTerm={debouncedQ}
          onNew={() => navigate("/architect/designs/new")}
        />
      )}

      {hasResults && (
        <div className="mt-5 space-y-2" data-testid="designs-list">
          {filtered.map((d) => (
            <DesignRow
              key={d.id}
              design={d}
              userId={user?.id}
              onEdited={(newId) =>
                navigate(`/architect/designs/new?draft=${newId}`)
              }
            />
          ))}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
function SectionHeader({ eyebrow, title }) {
  return (
    <div style={{ marginTop: 16 }}>
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 22,
            height: 2,
            background: "#B14E2F",
          }}
        />
        <span className="wn-label" style={{ color: "#8F3C23" }}>
          {eyebrow}
        </span>
      </div>
      <h2
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: 18,
          fontWeight: 500,
          letterSpacing: "-0.005em",
          color: "#1B1F4B",
          lineHeight: 1.2,
          margin: "6px 0 0",
        }}
      >
        {title}
      </h2>
    </div>
  );
}

function summaryLine(data, filter, isSearching, q, visibleN) {
  if (isSearching) {
    return visibleN === 0
      ? `No matches for "${q}".`
      : `${visibleN} match${visibleN === 1 ? "" : "es"} for "${q}".`;
  }
  if (filter !== "all") {
    const label = FILTERS.find((f) => f.key === filter)?.label || filter;
    return `Filtered to ${label}.`;
  }
  const total = data.total || 0;
  if (total === 0) return "No designs yet.";
  const approved = data.counts?.Approved || 0;
  const needs = data.counts?.["Needs Revision"] || 0;
  const parts = [`${total} design${total === 1 ? "" : "s"} on file`];
  if (approved) parts.push(`${approved} approved`);
  if (needs) parts.push(`${needs} need revision`);
  return parts.join(" · ") + ".";
}

function pillStyle(isOn) {
  return {
    padding: "6px 13px",
    borderRadius: 999,
    border: `1px solid ${isOn ? "#1B1F4B" : "#D9D8E4"}`,
    background: isOn ? "#E6E7F1" : "#FBFAFD",
    color: isOn ? "#1B1F4B" : "#272636",
    fontFamily: '"Nunito Sans", sans-serif',
    fontWeight: isOn ? 700 : 600,
    fontSize: 11.5,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    cursor: "pointer",
    lineHeight: 1,
  };
}

function EmptyStateCard({ filter, isSearching, searchTerm, onNew }) {
  const copy = isSearching
    ? `Nothing matches "${searchTerm}".`
    : FILTER_EMPTY_COPY[filter] || FILTER_EMPTY_COPY.all;
  return (
    <div
      className="mt-8 wn-card"
      style={{ padding: "32px 24px", textAlign: "center" }}
      data-testid="designs-empty"
    >
      <FileText size={32} style={{ color: "#9AA0C0", margin: "0 auto" }} />
      <h3
        className="mt-3"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 500,
          fontSize: 19,
          color: "#1B1F4B",
        }}
      >
        {copy}
      </h3>
      {!isSearching && filter === "all" && (
        <button
          className="wn-btn wn-btn-primary mt-4"
          onClick={onNew}
          data-testid="designs-empty-submit-btn"
        >
          Submit a new design
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function DesignRow({ design, userId, onEdited }) {
  const photo = design.photo
    ? assetUrl(design.photo)
    : design.exterior_photo
      ? assetUrl(design.exterior_photo)
      : null;
  const typology = humanTypology(design.typology);
  const style = humanStyle(design.style);
  const perf = design.performance || {};
  const status = design.status;
  const isApproved = status === "Approved";
  const isEditable = status === "Draft" || status === "Needs Revision";
  const isVersion = !!design.parent_design_id;
  const [busy, setBusy] = useState(false);

  async function handleNewVersion(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!userId || busy) return;
    setBusy(true);
    try {
      const next = await openNewVersion(design.id, userId);
      toast.success(`Opened version ${next.version || 2} as a Draft.`);
      onEdited && onEdited(next.id);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not open new version.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="wn-card"
      style={{
        padding: "18px 20px",
        display: "grid",
        // Fixed widths on trailing columns so every row aligns identically.
        gridTemplateColumns:
          "minmax(0, 1.6fr) minmax(0, 1.1fr) minmax(0, 1.5fr) 130px 132px",
        alignItems: "start",
        gap: 20,
      }}
      data-testid={`design-row-${design.id}`}
    >
      {/* Thumbnail + name + style */}
      <Link
        to={`/marketplace/${design.id}`}
        className="flex items-center min-w-0"
        style={{ gap: 14, textDecoration: "none" }}
      >
        <span
          aria-hidden
          style={{
            width: 52,
            height: 52,
            borderRadius: 6,
            backgroundColor: "#ECEBF1",
            backgroundImage: photo ? `url(${photo})` : "none",
            backgroundSize: "cover",
            backgroundPosition: "center",
            flexShrink: 0,
            border: "1px solid #D9D8E4",
          }}
        />
        <div className="min-w-0">
          <div
            className="flex items-center"
            style={{ gap: 6, minWidth: 0 }}
          >
            <div
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 600,
                fontSize: 16,
                color: "#1B1F4B",
                lineHeight: 1.25,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {design.name}
            </div>
            {(design.version && design.version > 1) || isVersion ? (
              <span
                className="inline-flex items-center"
                style={{
                  gap: 3,
                  padding: "1px 7px",
                  borderRadius: 999,
                  background: "#E6E7F1",
                  color: "#1B1F4B",
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  flexShrink: 0,
                  lineHeight: 1.4,
                }}
                title="Design version"
                data-testid={`design-version-${design.id}`}
              >
                <Layers size={10} strokeWidth={2.4} />
                v{design.version || 2}
              </span>
            ) : null}
          </div>
          {style && (
            <div
              style={{
                color: "#6C6B85",
                fontSize: 12.5,
                marginTop: 2,
                fontFamily: '"Nunito Sans", sans-serif',
              }}
            >
              {style}
            </div>
          )}
        </div>
      </Link>

      {/* Typology + sqft + beds/baths */}
      <div className="min-w-0" style={{ paddingTop: 4 }}>
        <div
          style={{
            color: "#272636",
            fontSize: 13,
            fontWeight: 600,
            fontFamily: '"Nunito Sans", sans-serif',
          }}
        >
          {typology || "—"}
        </div>
        <div
          className="tabular-nums"
          style={{
            color: "#6C6B85",
            fontSize: 12.5,
            marginTop: 2,
            fontFamily: '"Nunito Sans", sans-serif',
          }}
        >
          {design.sqft ? `${design.sqft.toLocaleString()} sqft` : ""}
          {design.beds ? ` · ${design.beds}bd / ${design.baths || 0}ba` : ""}
        </div>
      </div>

      {/* Performance (or draft resume meta for editable rows) */}
      <div className="min-w-0" style={{ paddingTop: 4 }}>
        {isEditable ? (
          <DraftResumeMeta design={design} />
        ) : (
          <PerformanceLine perf={perf} />
        )}
      </div>

      {/* Status */}
      <div className="flex items-start justify-end" style={{ paddingTop: 2 }}>
        <StatusPill
          value={status}
          testId={`design-status-${status.replace(/\s+/g, "_")}`}
        />
      </div>

      {/* Row action */}
      <div className="flex items-center justify-end" style={{ gap: 6 }}>
        {isApproved && (
          <button
            type="button"
            className="wn-btn wn-btn-tertiary"
            onClick={handleNewVersion}
            disabled={busy}
            style={{ paddingLeft: 8, paddingRight: 8, fontSize: 12 }}
            data-testid={`design-new-version-${design.id}`}
            title="Open a new version of this approved design"
          >
            <Pencil size={12} /> New version
          </button>
        )}
        {isEditable && (
          <Link
            to={`/architect/designs/new?draft=${design.id}`}
            className="wn-btn wn-btn-tertiary"
            style={{ paddingLeft: 8, paddingRight: 8, fontSize: 12 }}
            data-testid={`design-resume-${design.id}`}
          >
            <Pencil size={12} /> Resume
          </Link>
        )}
        <Link
          to={`/marketplace/${design.id}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: 4,
            color: "#9AA0C0",
          }}
          aria-label="Open design"
        >
          <ArrowUpRight size={14} />
        </Link>
      </div>
    </div>
  );
}

function PerformanceLine({ perf }) {
  const { inquiries = 0, saves = 0, purchases = 0 } = perf;
  const isQuiet = inquiries + saves + purchases === 0;
  if (isQuiet) {
    return (
      <div
        style={{
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          color: "#9AA0C0",
          fontSize: 12.5,
          lineHeight: 1.4,
        }}
        data-testid="design-perf-quiet"
      >
        No marketplace activity yet · last 30 days.
      </div>
    );
  }
  return (
    <div data-testid="design-perf">
      <div
        className="inline-flex items-center"
        style={{
          gap: 6,
          color: "#3A5563",
          fontSize: 13,
          fontFamily: '"Nunito Sans", sans-serif',
          fontWeight: 600,
        }}
      >
        <TrendingUp size={12} strokeWidth={2} style={{ color: "#4F6149" }} />
        <span className="tabular-nums">{inquiries}</span> inquir{inquiries === 1 ? "y" : "ies"}
        <span style={{ color: "#9AA0C0" }}>·</span>
        <span className="tabular-nums">{saves}</span> save{saves === 1 ? "" : "s"}
        <span style={{ color: "#9AA0C0" }}>·</span>
        <span className="tabular-nums">{purchases}</span> purchase{purchases === 1 ? "" : "s"}
      </div>
      <div
        style={{
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          color: "#9AA0C0",
          fontSize: 11.5,
          marginTop: 2,
        }}
      >
        last 30 days
      </div>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function humanTypology(t) {
  if (!t) return null;
  const map = {
    single_family: "Single family",
    multi_family: "Multi-family",
    adu: "ADU",
    duplex: "Duplex",
    townhouse: "Townhouse",
    cottage: "Cottage",
    studio: "Studio",
  };
  return map[t] || t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function humanStyle(s) {
  if (!s) return null;
  const map = {
    shed_modern: "Shed Modern",
    modern_adu: "Modern · ADU",
    modern: "Modern",
    traditional: "Traditional",
    craftsman: "Craftsman",
    farmhouse: "Farmhouse",
    contemporary: "Contemporary",
  };
  return map[s] || s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const STEP_TOTAL = 6;
const STEP_NAMES = [
  "About the design",
  "Specifications",
  "Cost data",
  "Images",
  "Parcel verification",
  "Review",
];

function relativeTime(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const delta = Math.max(0, Date.now() - then);
  const m = Math.round(delta / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.round(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.round(mo / 12)}y ago`;
}

function DraftResumeMeta({ design }) {
  const stepRaw = Number(design.draft_step);
  const step = stepRaw >= 1 && stepRaw <= STEP_TOTAL ? stepRaw : null;
  const stepLabel = step ? STEP_NAMES[step - 1] : null;
  const when = relativeTime(design.updated_at || design.created_at);
  return (
    <div
      className="min-w-0"
      data-testid={`design-resume-meta-${design.id}`}
    >
      <div
        className="inline-flex items-center"
        style={{
          gap: 6,
          color: "#3A5563",
          fontSize: 13,
          fontFamily: '"Nunito Sans", sans-serif',
          fontWeight: 600,
        }}
      >
        <Pencil size={12} strokeWidth={2} style={{ color: "#B14E2F" }} />
        <span className="tabular-nums">
          Step {step || 1} of {STEP_TOTAL}
        </span>
        {stepLabel && (
          <>
            <span style={{ color: "#9AA0C0" }}>·</span>
            <span
              style={{
                color: "#6C6B85",
                fontWeight: 500,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 180,
              }}
              title={stepLabel}
            >
              {stepLabel}
            </span>
          </>
        )}
      </div>
      {when && (
        <div
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#9AA0C0",
            fontSize: 11.5,
            marginTop: 2,
          }}
        >
          last edited {when}
        </div>
      )}
    </div>
  );
}
